import { uniq } from 'es-toolkit';
import React, { FunctionComponent } from 'react';
import { SxProps, Theme } from '@mui/material';

import { INDETERMINATE, isIndeterminate, PropertySelectField } from '@paragon/client-tools';
import { BottomBevelCut } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { NonNullBottomBevelCutProperties } from './SingleBevelCutProperties';

interface Props {
  bevelCuts: readonly (BottomBevelCut | null)[];
  disabled: boolean;
  propertyName: string;
  readonly sx?: SxProps<Theme>;
  onEditBevelCut: (newBevelCut: BottomBevelCut | null) => void;
}

type BevelCutType = 'None' | 'Front' | 'Back';
function getBevelCutType(bevelCut: BottomBevelCut | null): BevelCutType {
  return bevelCut?.cutSide ?? 'None';
}

export const BottomBevelCutProperties: FunctionComponent<Props> = (props: Props) => {
  const { bevelCuts, disabled, propertyName, sx, onEditBevelCut } = props;

  const bevelCut = summarize(bevelCuts);

  const bevelCutTypes = ['None', 'Front', 'Back'] as const;

  return (
    <>
      <PropertySelectField<BevelCutType>
        property={propertyName}
        value={isIndeterminate(bevelCut) ? INDETERMINATE : getBevelCutType(bevelCut)}
        sx={sx}
        disabled={disabled}
        options={bevelCutTypes}
        submit={(value) => onEditBevelCut(value === 'None' ? null : { cutSide: value, angle: 45 })}
      />
      {bevelCut != null && !isIndeterminate(bevelCut) ? (
        <NonNullBottomBevelCutProperties bevelCut={bevelCut} disabled={disabled} onEditBevelCut={onEditBevelCut} />
      ) : null}
    </>
  );
};

function summarize(bevelCuts: readonly (BottomBevelCut | null)[]) {
  if (bevelCuts.every(notNullish)) {
    return uniq(bevelCuts.map((cut) => cut.angle)).length === 1 &&
      uniq(bevelCuts.map((cut) => cut.cutSide)).length === 1
      ? bevelCuts[0]
      : INDETERMINATE;
  } else if (bevelCuts.some(notNullish)) {
    return INDETERMINATE;
  } else {
    return null;
  }
}
